<script setup lang="ts">
import { mdiChevronRight, mdiEyeOff, mdiEye } from '@mdi/js'
import { AuthMode } from '~/types/interfaces'
import type { AuthChallengeSession } from '~/types/interfaces'
import type { ModeParams } from './useAuthFlow'
import { offlineUsers, passwordRules } from './useAuthFlow'

defineOptions({ name: 'AuthForm' })

defineProps<{
  mode: AuthMode
  modeParams: ModeParams
  loading: boolean
  authChallengeSession: AuthChallengeSession | undefined
}>()

const username = defineModel<string>('username', { required: true })
const password = defineModel<string>('password', { required: true })
const passwordConfirm = defineModel<string>('passwordConfirm', { required: true })
const code = defineModel<string>('code', { required: true })
const visible = defineModel<boolean>('visible', { required: true })
const agreedModel = defineModel<boolean>('agreed', { required: true })

const emit = defineEmits<{
  submit: []
}>()
</script>

<template>
  <v-card
    class="mx-auto pa-6 pa-sm-12 pb-4 pb-sm-8"
    elevation="8"
    max-width="448"
    rounded="lg"
  >
    <!-- Social login buttons (rendered above form fields) -->
    <slot name="social-login" />

    <!-- SIGNUP: email input with under-18 dialog -->
    <template v-if="mode === AuthMode.SIGNUP">
      <div
        class="text-subtitle-1 text-medium-emphasis d-flex flex-wrap align-center justify-space-between"
      >
        メールアドレス

        <v-dialog max-width="500">
          <template #activator="{ props: activatorProps }">
            <v-btn
              v-bind="activatorProps"
              color="blue"
              size="small"
              class="pr-0"
              variant="text"
            >
              18歳未満ですか？
            </v-btn>
          </template>

          <template #default="{ isActive }">
            <v-card title="18歳未満の方のアカウントについて">
              <v-card-text>
                18歳未満の方は、まず保護者の方がアカウントを作成してください。<br>
                その後、保護者の方のアカウントでサインインし、18歳未満の方のアカウントを作成してください。
              </v-card-text>

              <v-card-actions>
                <v-spacer />

                <v-btn
                  text="とじる"
                  @click="isActive.value = false"
                />
              </v-card-actions>
            </v-card>
          </template>
        </v-dialog>
      </div>
      <v-text-field
        v-model="username"
        density="compact"
        placeholder="メールアドレス"
        variant="outlined"
        autocomplete="username"
      />
    </template>

    <!-- RESEND / FORGOTPW / forceOpenUsername: email input -->
    <template
      v-if="
        mode === AuthMode.RESEND
          || mode === AuthMode.FORGOTPW
          || modeParams.forceOpenUsername
      "
    >
      <div
        class="text-subtitle-1 text-medium-emphasis d-flex align-center justify-space-between"
      >
        メールアドレス
      </div>
      <v-text-field
        v-model="username"
        density="compact"
        placeholder="メールアドレス"
        variant="outlined"
        autocomplete="username"
      />
    </template>

    <!-- SIGNIN: email or username input -->
    <template v-if="mode === AuthMode.SIGNIN">
      <div class="text-subtitle-1 text-medium-emphasis">
        メールアドレスまたはユーザー名
      </div>
      <v-text-field
        v-model="username"
        density="compact"
        placeholder="メールアドレスまたはユーザー名"
        variant="outlined"
        autocomplete="username"
      />
    </template>

    <!-- RESETPW: disabled username display -->
    <template v-if="mode === AuthMode.RESETPW">
      <div
        class="text-subtitle-1 text-medium-emphasis d-flex align-center justify-space-between"
      >
        メールアドレスまたはユーザー名
      </div>
      <v-text-field
        v-model="username"
        density="compact"
        variant="outlined"
        disabled
      >
        {{ authChallengeSession?.username }}
      </v-text-field>
    </template>

    <!-- Password label with "forgot password?" link -->
    <template
      v-if="
        mode === AuthMode.SIGNUP
          || mode === AuthMode.SIGNIN
          || mode === AuthMode.SETNEWPW
      "
    >
      <div
        class="text-subtitle-1 text-medium-emphasis d-flex flex-wrap align-center justify-space-between"
      >
        パスワード
        <NuxtLink
          class="text-caption text-decoration-none text-blue"
          to="/forgotpw"
          tabindex="-1"
        >
          パスワードを忘れましたか？</NuxtLink>
      </div>
    </template>
    <template v-if="mode === AuthMode.RESETPW">
      <div
        class="text-subtitle-1 text-medium-emphasis d-flex align-center justify-space-between"
      >
        パスワード
      </div>
    </template>

    <!-- Password input field -->
    <template
      v-if="
        mode === AuthMode.SIGNUP
          || mode === AuthMode.SIGNIN
          || mode === AuthMode.SETNEWPW
          || mode === AuthMode.RESETPW
      "
    >
      <v-text-field
        v-model="password"
        :append-inner-icon="visible ? mdiEyeOff : mdiEye"
        :type="visible ? 'text' : 'password'"
        density="compact"
        placeholder="パスワード"
        variant="outlined"
        :rules="passwordRules"
        validate-on="input"
        autocomplete="new-password"
        @click:append-inner="visible = !visible"
      />
    </template>

    <!-- Password confirmation -->
    <template
      v-if="
        mode === AuthMode.SIGNUP
          || mode === AuthMode.SETNEWPW
          || mode === AuthMode.RESETPW
      "
    >
      <div
        class="text-subtitle-1 text-medium-emphasis d-flex align-center justify-space-between"
      >
        パスワード（確認用）
      </div>
      <v-text-field
        v-model="passwordConfirm"
        :append-inner-icon="visible ? mdiEyeOff : mdiEye"
        :type="visible ? 'text' : 'password'"
        density="compact"
        placeholder="パスワード（確認用）"
        variant="outlined"
        autocomplete="new-password"
        @click:append-inner="visible = !visible"
      />
    </template>

    <!-- Verification code -->
    <template v-if="mode === AuthMode.CONFIRM || mode === AuthMode.RESETPW">
      <div class="text-subtitle-1 text-medium-emphasis">
        検証コード
      </div>
      <p>メールに送られた検証コードを入力してください</p>
      <v-text-field
        v-model="code"
        density="compact"
        placeholder="検証コード"
        variant="outlined"
        autocomplete="one-time-code"
      />
    </template>

    <!-- Offline user selector -->
    <template v-if="mode === AuthMode.OFFLINE">
      <v-select
        v-model="username"
        label="ユーザー"
        density="compact"
        variant="outlined"
        :items="offlineUsers"
        item-title="label"
        item-value="id"
        placeholder="ユーザーを選択"
      />
    </template>

    <!-- Terms of service checkbox -->
    <v-checkbox
      v-if="mode === AuthMode.SIGNUP"
      v-model="agreedModel"
      density="compact"
      class="mb-2"
    >
      <template #label>
        <span class="text-body-2">
          <NuxtLink
            to="/docs/termsofuse"
            target="_blank"
            class="text-blue"
          >利用規約</NuxtLink>
          および
          <NuxtLink
            to="/docs/privacypolicy"
            target="_blank"
            class="text-blue"
          >プライバシーポリシー</NuxtLink>
          に同意します
        </span>
      </template>
    </v-checkbox>

    <!-- Submit button -->
    <v-btn
      class="mb-8"
      color="blue"
      size="large"
      variant="tonal"
      block
      :disabled="mode === AuthMode.SIGNUP && !agreedModel"
      @click="emit('submit')"
    >
      <template v-if="loading">
        <v-progress-circular
          class="mx-auto"
          indeterminate
          color="blue"
        />
      </template>
      <template v-else>
        {{ modeParams.title }}
      </template>
    </v-btn>

    <!-- Navigation links -->
    <v-card-text class="text-center">
      <template
        v-if="
          mode === AuthMode.SIGNUP
            || mode === AuthMode.RESEND
            || mode === AuthMode.RESETPW
        "
      >
        <NuxtLink
          class="text-blue text-decoration-none"
          to="/signin"
        >
          登録済の方はサインイン <v-icon>{{ mdiChevronRight }}</v-icon>
        </NuxtLink>
      </template>

      <template v-if="mode === AuthMode.SIGNIN || mode === AuthMode.FORGOTPW">
        <NuxtLink
          class="text-blue text-decoration-none"
          to="/signup"
        >
          はじめての方はサインアップ
          <v-icon>{{ mdiChevronRight }}</v-icon>
        </NuxtLink>
      </template>

      <template v-if="mode === AuthMode.CONFIRM">
        <NuxtLink
          class="text-blue text-decoration-none"
          to="/resend"
        >
          検証コードを再送 <v-icon>{{ mdiChevronRight }}</v-icon>
        </NuxtLink>
      </template>
    </v-card-text>
  </v-card>
</template>
